import { useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { DocList } from '../components/DocList'
import { PromptDialog } from '../components/PromptDialog'
import { useDocumentsByProject, useFolderTree } from '../lib/queries'
import { apiPost } from '../lib/api'
import { usePageTitle } from '../lib/usePageTitle'
import type { DocumentDto, FolderDto, ProjectDto } from '../lib/types'

export function ProjectHome() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const ctx = useOutletContext<{ project: ProjectDto | undefined }>()
  const p = ctx.project

  const folders = useFolderTree(p?.uuid)
  const docs = useDocumentsByProject(p?.uuid)

  const [params, setParams] = useSearchParams()
  const selectedFolderUuid = params.get('folder')
  const [newDocOpen, setNewDocOpen] = useState(false)
  const [createDocFolderUuid, setCreateDocFolderUuid] = useState<string | null>(null)

  // sidebar 의 'New document' 가 navigate(?folder=...&new=doc) 로 신호 — 자동 modal 오픈
  useEffect(() => {
    const f = params.get('folder')
    if (params.get('new') === 'doc' && f) {
      setCreateDocFolderUuid(f)
      setNewDocOpen(true)
      const next = new URLSearchParams(params)
      next.delete('new')
      setParams(next, { replace: true })
    }
  }, [params, setParams])

  const allDocs = docs.data ?? []
  const allFolders = folders.data ?? []

  const folderById = new Map<number, FolderDto>()
  const walkFolders = (arr: FolderDto[]) => { for (const f of arr) { folderById.set(f.id, f); if (f.children) walkFolders(f.children) } }
  walkFolders(allFolders)
  const selectedFolder = selectedFolderUuid ? Array.from(folderById.values()).find((f) => f.uuid === selectedFolderUuid) : null
  const filteredDocs = selectedFolder
    ? allDocs.filter((d) => d.folderId === selectedFolder.id)
    : allDocs.filter((d) => d.folderId == null)

  const headerTitle = selectedFolder ? selectedFolder.name : (p?.name ?? 'Documents')
  usePageTitle(selectedFolder ? selectedFolder.name : p?.name)
  const requestNewDoc = (folderUuid?: string) => {
    if (folderUuid) { setCreateDocFolderUuid(folderUuid); setNewDocOpen(true); return }
    if (!selectedFolder) { window.alert('Select a folder first.'); return }
    setCreateDocFolderUuid(selectedFolder.uuid); setNewDocOpen(true)
  }
  const onCreateDoc = async (title: string) => {
    if (!title || !createDocFolderUuid || !p) { setNewDocOpen(false); return }
    const doc = await apiPost<DocumentDto>(`/api/projects/${p.uuid}/documents`, {
      title, content: '', folderUuid: createDocFolderUuid,
    })
    setNewDocOpen(false)
    setCreateDocFolderUuid(null)
    qc.invalidateQueries({ queryKey: ['projects', p.uuid, 'documents'] })
    navigate(`/projects/${encodeURIComponent(p.projectKey)}/d/${doc.uuid}/edit`)
  }

  return (
    <main className="split-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="project-home-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <h2 style={{ margin: 0 }}>{headerTitle}</h2>
        <button id="btn-new-doc" className="btn btn-primary" onClick={() => requestNewDoc()}>+ New Document</button>
      </div>
      <DocList docs={filteredDocs} folders={allFolders} />

      <PromptDialog
        open={newDocOpen}
        title="Create Document"
        id="modal-new-doc"
        inputId="doc-title"
        confirmButtonId="btn-create-doc"
        label="Document title"
        confirmText="Create"
        onSubmit={onCreateDoc}
        onCancel={() => setNewDocOpen(false)}
      />
    </main>
  )
}
